import { Select, useChatFeedbackLock } from './FarChat/ChatFeedbackOverlays';
import { isWorkflowReadOnly } from '../utils/workflowAccess';
import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { format } from 'date-fns';
import type { FlowMetadata } from '../types/flow';
import { MOBILE_DENSITY, phoneMedia } from '../theme/mobileDensity';
import { supportsBusinessImpact } from '../utils/workflowSupport';
import {
  patchBusinessImpact as defaultPatchBusinessImpact,
  type BusinessImpactPatchPayload,
  type BusinessImpactPatchResponse,
} from '../api/businessImpactFunctions';
import {
  patchFlowMeta as defaultPatchFlowMeta,
  type FlowMetaPatchPayload,
  type FlowMetaPatchResponse,
} from '../api/flowFunctions';
import InlineEditableText from './InlineEditableText';
import ImpactBlock from './ImpactBlock';
import { Reveal, StaggerGroup } from './motion';
import {
  buildStatusChipLabel,
  completedDaysCount as sharedCompletedDaysCount,
  daysSinceCreated as sharedDaysSinceCreated,
} from '../utils/flowStatusChip';
import {
  deriveFlowStatus,
  deriveLastActiveStageText,
  lastActiveNumeric,
} from '../utils/flowTableProgress';

export {
  deriveFlowStatus,
  deriveLastActiveStageText,
  stageTotalFor,
} from '../utils/flowTableProgress';

function hasMarketResearchEditAwareness(flow: FlowMetadata): boolean {
  return (flow.workflow_type ?? 'market_research') === 'market_research'
    && flow.market_research_has_unreviewed_other_edit === true;
}

export type FlowSortField =
  | 'name'
  | 'workflow_type'
  | 'status'
  | 'last_active'
  | 'created'
  | 'updated';
export type FlowSortDir = 'asc' | 'desc';

// Fixed pixel widths for each table column. Must match between the column
// header and the accordion summary so they stay aligned. Using pixels (not
// flex) makes the layout fully deterministic and prevents long names or
// impact summaries from shifting neighboring columns.
export const FLOW_COL_WIDTHS = {
  name: 350,
  workflow_type: 220,
  status: 260,
  impact: 400,
  actions: 100,
};

type ChipColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'info'
  | 'warning'
  | 'error';

/**
 * Build a mode-aware `sx` for a workflow-type chip using the `cat.*` palette
 * token that matches the given workflow type. The chip gets a tinted background
 * wash and a matching text/border color so each workflow type is visually
 * distinguishable, mirroring the category-color coding on the homepage tiles.
 */
function catChipSx(
  catKey: keyof NonNullable<import('@mui/material/styles').PaletteOptions['cat']>,
): SxProps<Theme> {
  return {
    borderColor: (theme: Theme) => theme.palette.cat[catKey],
    color: (theme: Theme) => theme.palette.cat[catKey],
    backgroundColor: (theme: Theme) =>
      `${theme.palette.cat[catKey]}14`, // ~8% opacity tint
  };
}

export function getFlowTypeChip(
  flow: FlowMetadata
): { label: string; color: ChipColor; sx?: SxProps<Theme> } {
  // Legacy flows created before the multi-workflow system have an
  // empty/missing ``workflow_type``. Treat them as Market Research for
  // display purposes (read-time fallback only; metadata is not mutated).
  const wf = flow.workflow_type || 'market_research';
  if (wf === 'clause_logic')
    return { label: 'CLS', color: 'default', sx: catChipSx('clauseLogic') };
  if (wf === 'sow_verification')
    return { label: 'Construction SOW Verification', color: 'default', sx: catChipSx('sowVerification') };
  if (wf === 'services_pws_generation')
    return { label: 'Services PWS Generation', color: 'default', sx: catChipSx('services') };
  if (wf === 'wage_determination')
    return { label: 'Wage Determination', color: 'default', sx: catChipSx('wageDet') };
  if (wf === 'pnm') return { label: 'PNM', color: 'default', sx: catChipSx('pnm') };
  if (wf === 'fopr')
    return {
      label: flow.standalone_mode ? 'Construction Abstract' : 'FOPR',
      color: 'default',
      sx: catChipSx('fopr'),
    };
  if (wf === 'services') return { label: 'One-time Services', color: 'default', sx: catChipSx('services') };
  if (wf === 'one_time_construction') return { label: flow.standalone_mode ? 'Price realism estimator' : 'One-time Construction', color: 'default', sx: catChipSx('sowVerification') };
  if (wf === 'services_combo') return { label: 'Complex Services Synopsis', color: 'default', sx: catChipSx('services') };
  if (wf === 'gpc') return { label: 'GPC', color: 'default', sx: catChipSx('gpc') };
  if (wf === 'other_transactions') return { label: 'Other Transactions', color: 'default' };
  if (wf === 'post_award_actions') {
    return { label: 'Post-award Actions', color: 'default' };
  }
  if (wf === 'dcma_crr') return { label: 'Contract Receipt and Review', color: 'default', sx: catChipSx('sowVerification') };
  if (wf === 'dcma_pbp')
    return { label: 'DCMA PBP Review', color: 'default', sx: catChipSx('sowVerification') };
  if (wf === 'complex_services')
    // Distinct MUI palette color (secondary cobalt blue) — the `cat.*` palette
    // has no `complexServices` token, so mirror `catChipSx`'s tinted-wash shape
    // inline using a palette color not used by services/services_combo/gpc.
    return {
      label: 'Complex Services',
      color: 'default',
      sx: {
        borderColor: (theme: Theme) => theme.palette.secondary.main,
        color: (theme: Theme) => theme.palette.secondary.main,
        backgroundColor: (theme: Theme) => `${theme.palette.secondary.main}14`, // ~8% opacity tint
      },
    };
  return {
    label: flow.standalone_mode ? 'Vendor Bids' : 'Market Research',
    color: 'default',
    sx: catChipSx('marketResearch'),
  };
}


function statusChipColor(status: 'completed' | 'in progress'): ChipColor {
  if (status === 'completed') return 'success';
  return 'warning';
}

function safeDate(iso?: string | null): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

function absoluteDate(iso?: string | null, withTime = true): string {
  const d = safeDate(iso);
  if (!d) return '—';
  try {
    return format(d, withTime ? 'MMM d, yyyy h:mm a' : 'MMM d, yyyy');
  } catch {
    return '—';
  }
}

/**
 * "Days to complete" cell value. Computes the duration between the flow's
 * creation and completion timestamps in whole days (standard rounding).
 *
 * Gating: only completed flows get a numeric value. A flow is considered
 * completed when `flow.completedAt` is set OR `flow.status === 'completed'`
 * (the latter covers legacy flows that finished before `completedAt` was
 * added). For non-completed flows (e.g. active or in-progress archive
 * rows) we return "-" — we don't want to show an in-progress duration
 * masquerading as a completion time.
 *
 * When `completedAt` is missing but the flow is marked completed
 * (`status === 'completed'`), we fall back to `updatedAt` to match the
 * legacy Scoreboard heuristic. Returns "-" when the required timestamps
 * are unavailable.
 */
export function formatDaysToComplete(flow: FlowMetadata): string {
  const isCompleted = deriveFlowStatus(flow) === 'completed';
  if (!isCompleted) return '-';
  const start = safeDate(flow.createdAt);
  const end = safeDate(flow.completedAt) ?? safeDate(flow.updatedAt);
  if (!start || !end) return '-';
  const diffMs = end.getTime() - start.getTime();
  if (diffMs < 0) return '-';
  const days = diffMs / 86400000; // ms per day
  return `${Math.round(days)} days`;
}

/**
 * DCMA reviews become immutable after their final decision. Archived DCMA
 * reviews are also retained records, including those archived before
 * completion, so neither state offers the generic destructive Delete action.
 */
export function isImmutableDcmaPbpFlow(flow: FlowMetadata): boolean {
  return (
    flow.workflow_type === 'dcma_pbp' &&
    (flow.archived === true || deriveFlowStatus(flow) === 'completed')
  );
}

/**
 * Re-exported from `utils/flowStatusChip` so existing callers
 * (and tests) that import these from `FlowTable` keep working. The grid view
 * imports the helpers directly from the utility module.
 */
export const completedDaysCount = sharedCompletedDaysCount;
export const daysSinceCreated = sharedDaysSinceCreated;

export function sortFlows(
  flows: FlowMetadata[],
  field: FlowSortField,
  dir: FlowSortDir
): FlowMetadata[] {
  const mul = dir === 'asc' ? 1 : -1;
  const copy = [...flows];
  copy.sort((a, b) => {
    let av: any;
    let bv: any;
    switch (field) {
      case 'name':
        av = (a.name || '').toLowerCase();
        bv = (b.name || '').toLowerCase();
        break;
      case 'workflow_type':
        av = (a.workflow_type || '').toLowerCase();
        bv = (b.workflow_type || '').toLowerCase();
        break;
      case 'status':
        av = deriveFlowStatus(a);
        bv = deriveFlowStatus(b);
        break;
      case 'created':
        av = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        bv = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        break;
      case 'last_active':
        av = lastActiveNumeric(a);
        bv = lastActiveNumeric(b);
        break;
      case 'updated':
      default:
        av = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        bv = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        break;
    }
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * mul;
    const as = String(av);
    const bs = String(bv);
    if (as < bs) return -1 * mul;
    if (as > bs) return 1 * mul;
    return 0;
  });
  return copy;
}

// ----- Sort controls -----

interface FlowSortControlsProps {
  sortField: FlowSortField;
  sortDir: FlowSortDir;
  onSortFieldChange: (f: FlowSortField) => void;
  onSortDirChange: (d: FlowSortDir) => void;
}

export const FlowSortControls: React.FC<FlowSortControlsProps> = ({
  sortField,
  sortDir,
  onSortFieldChange,
  onSortDirChange,
}) => (
  <Box data-testid="flow-sort-controls" sx={(theme) => ({ width: 205, [phoneMedia(theme)]: { width: '100%' } })}>
    <FormControl size="small" fullWidth>
      <InputLabel id="flow-sort-by-label">Sort by</InputLabel>
      <Select
        value={`${sortField}:${sortDir}`}
        labelId="flow-sort-by-label"
        label="Sort by"
        onChange={(e) => {
          const [field, dir] = (e.target.value as string).split(':');
          onSortFieldChange(field as FlowSortField);
          onSortDirChange(dir as FlowSortDir);
        }}
      >
        <MenuItem value="name:asc">Name ↑</MenuItem>
        <MenuItem value="name:desc">Name ↓</MenuItem>
        <MenuItem value="workflow_type:asc">Type ↑</MenuItem>
        <MenuItem value="workflow_type:desc">Type ↓</MenuItem>
        <MenuItem value="status:asc">Status ↑</MenuItem>
        <MenuItem value="status:desc">Status ↓</MenuItem>
        <MenuItem value="last_active:asc">Stages Complete ↑</MenuItem>
        <MenuItem value="last_active:desc">Stages Complete ↓</MenuItem>
        <MenuItem value="created:asc">Created ↑</MenuItem>
        <MenuItem value="created:desc">Created ↓</MenuItem>
        <MenuItem value="updated:asc">Last Update ↑</MenuItem>
        <MenuItem value="updated:desc">Last Update ↓</MenuItem>
      </Select>
    </FormControl>
  </Box>
);

// ----- Column header -----

// Width reserved at the right edge of each row for the explicit expand IconButton
// so that the header "Actions" column and the row's Actions cell can share the
// same flex weight and still line up above/below each other.
const EXPAND_BUTTON_WIDTH = 40;

export const FlowTableHeader: React.FC = () => (
  <Box
    sx={(theme) => ({
      display: 'flex',
      alignItems: 'center',
      px: 2,
      py: 1,
      bgcolor: 'surfaceMuted',
      borderRadius: 1,
      mb: 0.5,
      [phoneMedia(theme)]: { display: 'none' },
    })}
  >
    <Typography
      variant="caption"
      fontWeight="bold"
      sx={{ width: `${FLOW_COL_WIDTHS.name}px`, flexShrink: 0, flexGrow: 0 }}
    >
      Name
    </Typography>
    <Typography
      variant="caption"
      fontWeight="bold"
      sx={{ width: `${FLOW_COL_WIDTHS.workflow_type}px`, flexShrink: 0, flexGrow: 0, pl: 1 }}
    >
      Type
    </Typography>
    <Typography
      variant="caption"
      fontWeight="bold"
      sx={{ width: `${FLOW_COL_WIDTHS.status}px`, flexShrink: 0, flexGrow: 0 }}
    >
      Status
    </Typography>
    <Typography
      variant="caption"
      fontWeight="bold"
      sx={{ width: `${FLOW_COL_WIDTHS.impact}px`, flexShrink: 0, flexGrow: 0 }}
    >
      Impact
    </Typography>
    <Typography
      variant="caption"
      fontWeight="bold"
      sx={{ width: `${FLOW_COL_WIDTHS.actions}px`, flexShrink: 0, flexGrow: 0, textAlign: 'right' }}
    >
      Actions
    </Typography>
    {/* Spacer matching the expand IconButton so "Actions" aligns with the
        row's actions cell even though the row has an extra expand button. */}
    <Box sx={{ width: `${EXPAND_BUTTON_WIDTH}px`, flexShrink: 0 }} />
  </Box>
);

// ----- Flow row (accordion) -----

interface FlowRowProps {
  flow: FlowMetadata;
  /** Presentation-only preview: show metadata without mounting mutators. */
  readOnly?: boolean;
  expanded: boolean;
  onToggle: () => void;
  onOpen?: (flow: FlowMetadata) => void;
  unreadNotificationCount?: number;
  onOpenNotifications?: (flow: FlowMetadata) => void;
  renderActions: (flow: FlowMetadata) => React.ReactNode;
  renderDetailActions?: (flow: FlowMetadata) => React.ReactNode;
  /**
   * Optional override for the patch helper used to persist Business Impact
   * edits (short summary in the row, paragraph in the expanded detail).
   * Defaults to the real `patchBusinessImpact` API helper. Tests inject a
   * mock here so they don't have to stub `fetch`.
   */
  onPatchBusinessImpact?: (
    flowId: string,
    payload: BusinessImpactPatchPayload
  ) => Promise<BusinessImpactPatchResponse>;
  /**
   * Notifies the parent that a Business Impact field was saved successfully.
   * The parent should merge `patch` into `flows[i].businessImpact` so the
   * row re-renders with the new value (otherwise `InlineEditableText`
   * snaps back to the stale `value` prop after save — see "Impact summary
   * disappears after save" bug).
   */
  onBusinessImpactUpdated?: (
    flowId: string,
    patch: { summaryShort?: string; summaryParagraph?: string; editedAt?: string }
  ) => void;
  /**
   * Generic successful-save notification for every editable saved-workflow
   * field. The owning list merges this patch into its canonical flow state.
   */
  onFlowUpdated?: (flowId: string, patch: Partial<FlowMetadata>) => void;
  /**
   * Optional override for the helper used to persist name/description edits.
   * Defaults to the real `patchFlowMeta` API helper. Tests inject a mock here
   * so they don't have to stub `fetch`. Mirrors `onPatchBusinessImpact`.
   */
  onPatchFlowMeta?: (
    flowId: string,
    payload: FlowMetaPatchPayload
  ) => Promise<FlowMetaPatchResponse>;
  /**
   * Notifies the parent that the flow's name and/or description was saved
   * successfully so it can merge the patch into `flows[i]` (otherwise
   * `InlineEditableText` snaps back to the stale `value` prop after save —
   * same pattern as `onBusinessImpactUpdated`).
   */
  onFlowMetaUpdated?: (
    flowId: string,
    patch: { name?: string; description?: string }
  ) => void;
}

export const FlowRow: React.FC<FlowRowProps> = ({
  flow,
  readOnly: globalReadOnly = false,
  expanded,
  onToggle,
  onOpen,
  unreadNotificationCount = 0,
  onOpenNotifications,
  renderActions,
  renderDetailActions,
  onPatchBusinessImpact,
  onBusinessImpactUpdated,
  onFlowUpdated,
  onPatchFlowMeta,
  onFlowMetaUpdated,
}) => {
  const feedbackLocked = useChatFeedbackLock();
  const feedbackLockRef = React.useRef(feedbackLocked); feedbackLockRef.current = feedbackLocked;
  const readOnly = globalReadOnly || isWorkflowReadOnly(flow);
  const currentReadOnly = React.useRef(readOnly); currentReadOnly.current = readOnly;
  const chip = getFlowTypeChip(flow);
  const status = deriveFlowStatus(flow);
  const lastActiveText = deriveLastActiveStageText(flow);
  const isOwner = flow.is_owner !== false;
  const hasEditAwareness = hasMarketResearchEditAwareness(flow);

  // Build the status chip label via the shared helper so the grid view
  // (FlowGridView) renders identical text. See `utils/flowStatusChip.ts`.
  const statusLabel = buildStatusChipLabel(flow, status, lastActiveText);

  // Resolve the BI patch helper (real fetch by default, tests can inject).
  const patchBI = onPatchBusinessImpact ?? defaultPatchBusinessImpact;
  const biSupported = supportsBusinessImpact(flow.workflow_type);
  const summaryShort = flow.businessImpact?.summaryShort ?? '';

  const handleImpactSave = React.useCallback(
    async (next: string) => {
      if (currentReadOnly.current) return;
      const resp = await patchBI(flow.id, { summaryShort: next });
      // Propagate the server response back up to the parent so the
      // authoritative `flows[i].businessImpact` is updated. We seed with
      // `summaryShort: next` so even if the backend ever omits it from the
      // payload we still keep the user-visible value (spread of `resp` lets
      // the server's authoritative value override if it differs).
      const businessImpact = { ...flow.businessImpact, summaryShort: next, ...resp };
      onFlowUpdated?.(flow.id, { businessImpact });
      onBusinessImpactUpdated?.(flow.id, businessImpact);
    },
    [flow.id, flow.businessImpact, patchBI, onBusinessImpactUpdated, onFlowUpdated]
  );

  // Resolve the flow-meta patch helper (real fetch by default, tests inject).
  const patchMeta = onPatchFlowMeta ?? defaultPatchFlowMeta;
  // Editing name/description is owner-or-shared, same access model as the
  // backend PATCH /api/flows/{id} endpoint and the Impact inline-edit.
  const handleNameSave = React.useCallback(
    async (next: string) => {
      if (currentReadOnly.current) return;
      // A workflow name must be non-empty (the backend also rejects empty
      // names with a 400). Rather than persist an invalid value or surface a
      // misleading "maintainers alerted" toast (the error contract squashes
      // non-canonical messages), treat an empty name as a no-op: skip the
      // round-trip and let InlineEditableText revert to the prior value.
      const trimmed = next.trim();
      if (!trimmed) {
        return;
      }
      const resp = await patchMeta(flow.id, { name: trimmed });
      onFlowUpdated?.(flow.id, {
        name: resp.name ?? trimmed,
        ...(resp.updatedAt ? { updatedAt: resp.updatedAt } : {}),
      });
      onFlowMetaUpdated?.(flow.id, { name: trimmed });
    },
    [flow.id, patchMeta, onFlowMetaUpdated, onFlowUpdated]
  );
  const handleDescriptionSave = React.useCallback(
    async (next: string) => {
      if (currentReadOnly.current) return;
      // Description may be emptied; only trailing whitespace is trimmed.
      const trimmed = next.trim();
      const resp = await patchMeta(flow.id, { description: trimmed });
      onFlowUpdated?.(flow.id, {
        description: resp.description ?? trimmed,
        ...(resp.updatedAt ? { updatedAt: resp.updatedAt } : {}),
      });
      onFlowMetaUpdated?.(flow.id, { description: trimmed });
    },
    [flow.id, patchMeta, onFlowMetaUpdated, onFlowUpdated]
  );

  return (
    <Accordion
      expanded={expanded}
      onChange={() => { /* controlled by explicit expand button */ }}
      disableGutters
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        mb: 0.5,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        disabled={feedbackLocked}
        aria-disabled={feedbackLocked || undefined}
        expandIcon={null}
        onClick={() => { if (!feedbackLockRef.current) onOpen?.(flow); }}
        sx={(theme) => ({
          px: 2,
          cursor: 'pointer',
          transition: 'none',
          '&:hover': { bgcolor: 'action.hover' },
          // Fully reset margin/padding on the inner content slot so MUI
          // defaults (which include `:first-of-type` and `.Mui-expanded`
          // overrides) can't introduce a horizontal offset for the first
          // row — the cause of the "first row shifted ~20px right" bug.
          '& .MuiAccordionSummary-content': { m: 0, p: 0, my: 1 },
          '& .MuiAccordionSummary-content.Mui-expanded': { m: 0, p: 0, my: 1 },
          [phoneMedia(theme)]: {
            px: MOBILE_DENSITY.cardInset,
            '& .MuiTypography-root': { whiteSpace: 'normal', overflowWrap: 'anywhere' },
            '& .MuiChip-root': { height: 'auto' },
            '& .MuiChip-label': { whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5 },
          },
        })}
      >
        <Box data-testid="flow-row-layout" sx={(theme) => ({ display: 'flex', alignItems: 'center', width: '100%', gap: 0, minWidth: 0, [phoneMedia(theme)]: { flexWrap: 'wrap', rowGap: 1 } })}>
          {readOnly && <Typography variant="caption" color="text.secondary">View only</Typography>}
          {/* Name column. The Name is the primary "open the flow" target, so —
              unlike the Impact cell — this cell does NOT stop row-open
              propagation. Clicking the name text bubbles up to the
              AccordionSummary and opens the flow, exactly as before inline edit
              existed. Only the pencil enters edit mode: InlineEditableText's
              pencil IconButton calls stopPropagation itself, and its edit-mode
              container also stops propagation, so editing never opens the flow. */}
          <Box
            sx={{ width: { xs: '100%', sm: `${FLOW_COL_WIDTHS.name}px` }, flexShrink: 0, flexGrow: 0, minWidth: 0, overflow: 'hidden', pr: 1, display: 'flex', alignItems: 'center' }}
          >
            <Box sx={{ minWidth: 0, flex: 1, display: 'flex', alignItems: 'center', gap: 0.25 }}>
              {readOnly ? (
                <Typography variant="body2" noWrap title={flow.name || flow.id}>
                  {flow.name || flow.id}
                </Typography>
              ) : (
                <InlineEditableText
                  value={flow.name || flow.id}
                  onSave={handleNameSave}
                  variant="single"
                  emptyPlaceholder={flow.id}
                  ariaLabel="Edit workflow name"
                  editButtonLabel="Edit workflow name"
                />
              )}
              {hasEditAwareness && (
                <Tooltip title="Updated by another collaborator">
                  <Box
                    component="span"
                    role="img"
                    aria-label={`${flow.name || 'Workflow'} has updates from another collaborator`}
                    data-testid={`workflow-edit-awareness-dot-${flow.id}`}
                    data-edit-awareness-tone="red"
                    sx={{
                      backgroundColor: (theme) => theme.palette.marketResearch.editAwareness,
                      border: 0,
                      borderRadius: '50%',
                      display: 'inline-block',
                      flexShrink: 0,
                      height: 10,
                      outline: 0,
                      width: 10,
                    }}
                  />
                </Tooltip>
              )}
              {unreadNotificationCount > 0 && onOpenNotifications && (
                <Tooltip title="Open workflow notifications">
                  <IconButton
                    size="small"
                    color="error"
                    aria-label={`${unreadNotificationCount} unread workflow notification${unreadNotificationCount === 1 ? '' : 's'} for ${flow.name || 'workflow'}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpenNotifications(flow);
                    }}
                  >
                    <Badge color="error" variant="dot">
                      <NotificationsNoneIcon fontSize="small" />
                    </Badge>
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
          <Box sx={{ width: { xs: '100%', sm: `${FLOW_COL_WIDTHS.workflow_type}px` }, flexShrink: 0, flexGrow: 0 }}>
            <Chip
              label={chip.label}
              size="small"
              variant="outlined"
              sx={{
                height: 22,
                ...(chip.sx as Record<string, unknown>),
              }}
            />
          </Box>
          {/* Combined Status + Last Active Stage chip. */}
          <Box sx={{ width: { xs: '100%', sm: `${FLOW_COL_WIDTHS.status}px` }, flexShrink: 0, flexGrow: 0 }}>
            <Chip
              label={statusLabel}
              size="small"
              color={statusChipColor(status)}
              sx={{ height: 22, textTransform: 'capitalize' }}
            />
          </Box>
          {/* Impact column. For workflow types that support BI (market_research,
              services), the cell is click-to-edit. The column truncates with
              ellipsis so users may type any length. For deferred types we
              render only a muted '-' with no edit affordance. */}
          <Box
            sx={{ width: { xs: '100%', sm: `${FLOW_COL_WIDTHS.impact}px` }, flexShrink: 0, flexGrow: 0, minWidth: 0, overflow: 'hidden', pr: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
            onClick={(e) => e.stopPropagation()}
          >
            {biSupported && readOnly ? (
              <Typography
                variant="body2"
                color={summaryShort.trim() ? 'text.primary' : 'text.secondary'}
                noWrap
                title={summaryShort || undefined}
              >
                {summaryShort || '-'}
              </Typography>
            ) : biSupported ? (
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <InlineEditableText
                  value={summaryShort}
                  onSave={handleImpactSave}
                  variant="single"
                  emptyPlaceholder="-"
                  ariaLabel="Edit impact summary"
                />
              </Box>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                noWrap
                title=""
                aria-label="No impact summary"
              >
                -
              </Typography>
            )}
          </Box>
          <Box
            sx={{ width: { xs: `calc(100% - ${MOBILE_DENSITY.touchTarget}px)`, sm: `${FLOW_COL_WIDTHS.actions}px` }, flexShrink: 0, flexGrow: 0, display: 'flex', flexWrap: { xs: 'wrap', sm: 'nowrap' }, justifyContent: { xs: 'flex-start', sm: 'flex-end' }, gap: 0.25 }}
            onClick={(e) => e.stopPropagation()}
          >
            {renderActions(flow)}
          </Box>
          <Box sx={{ width: { xs: MOBILE_DENSITY.touchTarget, sm: `${EXPAND_BUTTON_WIDTH}px` }, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip title="Show More Info">
              <IconButton
                size="small"
                aria-label={expanded ? 'Collapse details' : 'Expand details'}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle();
                }}
              >
                <ExpandMoreIcon
                  sx={{
                    transform: expanded ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s',
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: { xs: MOBILE_DENSITY.cardInset, sm: 2 }, pt: 1, pb: 2, bgcolor: 'background.default', overflowWrap: { xs: 'anywhere', sm: 'normal' } }}>
        {(() => {
          const detailActions = renderDetailActions ? renderDetailActions(flow) : null;
          if (!detailActions) return null;
          return (
            <Stack direction="row" spacing={0.5} sx={{ mb: 1, pr: { xs: 0, sm: '38px' }, flexWrap: { xs: 'wrap', sm: 'nowrap' }, rowGap: 0.5, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
              {detailActions}
            </Stack>
          );
        })()}
        <Stack spacing={0.5}>
          {!isOwner && flow.owner_email && (
            <Typography variant="body2">
              <strong>{readOnly ? 'Owner:' : 'Shared by:'}</strong> {flow.owner_email}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            <strong>Created:</strong> {absoluteDate(flow.createdAt, false)}
          </Typography>
          {(() => {
            // For completed flows, show "Completed at" using completedAt (or
            // updatedAt as a legacy fallback — same rule as
            // formatDaysToComplete). Otherwise show "Last updated" with
            // updatedAt.
            const isCompleted = deriveFlowStatus(flow) === 'completed';
            if (isCompleted) {
              const ts = flow.completedAt ?? flow.updatedAt;
              return (
                <Typography variant="body2" color="text.secondary">
                  <strong>Completed at:</strong> {absoluteDate(ts)}
                </Typography>
              );
            }
            return (
              <Typography variant="body2" color="text.secondary">
                <strong>Last updated:</strong> {absoluteDate(flow.updatedAt)}
              </Typography>
            );
          })()}
          {/* Description — click-to-edit (multi-line), mirroring the Impact
              paragraph editor in the expanded detail. Empty descriptions show
              a muted placeholder that is still editable via the pencil. */}
          <Box>
            <Typography variant="body2" component="div" sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
              <strong style={{ flexShrink: 0 }}>Description:</strong>
              <Box sx={{ minWidth: 0, flex: 1 }} onClick={(e) => e.stopPropagation()}>
                {readOnly ? (
                  <Typography variant="body2" color={flow.description ? 'text.primary' : 'text.secondary'}>
                    {flow.description || 'No description'}
                  </Typography>
                ) : (
                  <InlineEditableText
                    value={flow.description || ''}
                    onSave={handleDescriptionSave}
                    variant="multi"
                    emptyPlaceholder="No description"
                    ariaLabel="Edit workflow description"
                    editButtonLabel="Edit workflow description"
                  />
                )}
              </Box>
            </Typography>
          </Box>
          {biSupported && (readOnly ? (
            <Box
              sx={{
                mt: 1.5,
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'background.paper',
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                Impact
              </Typography>
              <Typography variant="body2" color={flow.businessImpact?.summaryParagraph ? 'text.primary' : 'text.secondary'} sx={{ mt: 0.5 }}>
                {flow.businessImpact?.summaryParagraph || 'No impact summary yet'}
              </Typography>
            </Box>
          ) : (
            <ImpactBlock
              flow={flow}
              onPatch={patchBI}
              onUpdated={(patch) => {
                const businessImpact = { ...flow.businessImpact, ...patch };
                onFlowUpdated?.(flow.id, { businessImpact });
                onBusinessImpactUpdated?.(flow.id, businessImpact);
              }}
            />
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

// ----- Whole table (header + rows) -----

interface FlowTableProps {
  flows: FlowMetadata[];
  /** Presentation-only preview: show metadata without mounting mutators. */
  readOnly?: boolean;
  sortField: FlowSortField;
  sortDir: FlowSortDir;
  expandedFlowId: string | null;
  onToggleExpand: (flowId: string) => void;
  onOpen?: (flow: FlowMetadata) => void;
  /** Counts are caller-private and server-projected for already listed flows. */
  unreadNotificationCounts?: Record<string, number>;
  onOpenNotifications?: (flow: FlowMetadata) => void;
  renderActions: (flow: FlowMetadata) => React.ReactNode;
  renderDetailActions?: (flow: FlowMetadata) => React.ReactNode;
  showHeader?: boolean;
  /** Test-only: inject a mock for the BI patch helper. */
  onPatchBusinessImpact?: (
    flowId: string,
    payload: BusinessImpactPatchPayload
  ) => Promise<BusinessImpactPatchResponse>;
  /**
   * Called after a Business Impact PATCH succeeds. Parent should merge
   * `patch` into `flows[i].businessImpact` so the row re-renders with the
   * updated value (fixes "Impact summary disappears after save").
   */
  onBusinessImpactUpdated?: (
    flowId: string,
    patch: { summaryShort?: string; summaryParagraph?: string; editedAt?: string }
  ) => void;
  /** Called after any editable field is saved successfully. */
  onFlowUpdated?: (flowId: string, patch: Partial<FlowMetadata>) => void;
  /** Test-only: inject a mock for the flow-meta patch helper. */
  onPatchFlowMeta?: (
    flowId: string,
    payload: FlowMetaPatchPayload
  ) => Promise<FlowMetaPatchResponse>;
  /**
   * Called after a name/description PATCH succeeds. Parent should merge
   * `patch` into `flows[i]` so the row re-renders with the updated value.
   */
  onFlowMetaUpdated?: (
    flowId: string,
    patch: { name?: string; description?: string }
  ) => void;
}

export const FlowTable: React.FC<FlowTableProps> = ({
  flows,
  readOnly = false,
  sortField,
  sortDir,
  expandedFlowId,
  onToggleExpand,
  onOpen,
  unreadNotificationCounts,
  onOpenNotifications,
  renderActions,
  renderDetailActions,
  showHeader = true,
  onPatchBusinessImpact,
  onBusinessImpactUpdated,
  onFlowUpdated,
  onPatchFlowMeta,
  onFlowMetaUpdated,
}) => {
  const sorted = sortFlows(flows, sortField, sortDir);
  return (
    // The white "card" background for the table rows must never shrink below
    // the fixed row width (sum of FLOW_COL_WIDTHS + EXPAND_BUTTON_WIDTH).
    // Without minWidth here, narrow viewports collapse this Box to <1240px
    // and the rows visually overflow the white background.
    <Box data-testid="flow-table" sx={(theme) => ({ minWidth: 1400, [phoneMedia(theme)]: { minWidth: 0, width: '100%' } })}>
      {showHeader && <FlowTableHeader />}
      <StaggerGroup>
        {sorted.map((flow) => (
          <Reveal key={flow.id}>
            <FlowRow
              flow={flow}
              readOnly={readOnly}
              expanded={expandedFlowId === flow.id}
              onToggle={() => onToggleExpand(flow.id)}
              onOpen={onOpen}
              unreadNotificationCount={unreadNotificationCounts?.[flow.id] ?? 0}
              onOpenNotifications={onOpenNotifications}
              renderActions={renderActions}
              renderDetailActions={renderDetailActions}
              onPatchBusinessImpact={onPatchBusinessImpact}
              onBusinessImpactUpdated={onBusinessImpactUpdated}
              onFlowUpdated={onFlowUpdated}
              onPatchFlowMeta={onPatchFlowMeta}
              onFlowMetaUpdated={onFlowMetaUpdated}
            />
          </Reveal>
        ))}
      </StaggerGroup>
    </Box>
  );
};

export default FlowTable;
